import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Range } from "react-range";
import { ArrowUpTrayIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useDeleteRoute } from "../../hooks/mutations/useDeleteRoute";
import { useUpdateRoute } from "../../hooks/mutations/useUpdateRoute";
import { useUploadRoute } from "../../hooks/mutations/useUploadRoute";
import { useFetchRoutes } from "../../hooks/queries/useFetchRoutes";
import ConfirmDialog from "../ui/ConfirmDialog";
import RouteOnMap from "../ui/RouteOnMap";
import { Spinner } from "../ui/Spinner";
import { Pagination } from "../ui/Pagination";
import SurfaceRatingBadge from "../ui/SurfaceRatingBadge";
import "leaflet/dist/leaflet.css";

function TrashActionIcon({ className }) {
  return (
    <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M2.80775 16.8845C2.31058 16.8845 1.885 16.7075 1.531 16.3535C1.177 15.9995 1 15.5739 1 15.0768V2.3845H0.75C0.5375 2.3845 0.359417 2.31258 0.21575 2.16875C0.0719168 2.02492 0 1.84675 0 1.63425C0 1.42158 0.0719168 1.2435 0.21575 1.1C0.359417 0.956333 0.5375 0.8845 0.75 0.8845H4.5C4.5 0.639667 4.58625 0.431001 4.75875 0.258501C4.93108 0.0861673 5.13967 0 5.3845 0H9.6155C9.86033 0 10.0689 0.0861673 10.2413 0.258501C10.4138 0.431001 10.5 0.639667 10.5 0.8845H14.25C14.4625 0.8845 14.6406 0.956417 14.7843 1.10025C14.9281 1.24408 15 1.42225 15 1.63475C15 1.84742 14.9281 2.0255 14.7843 2.169C14.6406 2.31267 14.4625 2.3845 14.25 2.3845H14V15.0768C14 15.5739 13.823 15.9995 13.469 16.3535C13.115 16.7075 12.6894 16.8845 12.1923 16.8845H2.80775ZM5.65425 13.3845C5.86675 13.3845 6.04483 13.3127 6.1885 13.169C6.332 13.0252 6.40375 12.847 6.40375 12.6345V5.1345C6.40375 4.922 6.33183 4.74383 6.188 4.6C6.04433 4.45633 5.86617 4.3845 5.6535 4.3845C5.441 4.3845 5.26292 4.45633 5.11925 4.6C4.97575 4.74383 4.904 4.922 4.904 5.1345V12.6345C4.904 12.847 4.97583 13.0252 5.1195 13.169C5.26333 13.3127 5.44158 13.3845 5.65425 13.3845ZM9.3465 13.3845C9.559 13.3845 9.73708 13.3127 9.88075 13.169C10.0243 13.0252 10.096 12.847 10.096 12.6345V5.1345C10.096 4.922 10.0242 4.74383 9.8805 4.6C9.73667 4.45633 9.55842 4.3845 9.34575 4.3845C9.13325 4.3845 8.95517 4.45633 8.8115 4.6C8.668 4.74383 8.59625 4.922 8.59625 5.1345V12.6345C8.59625 12.847 8.66817 13.0252 8.812 13.169C8.95567 13.3127 9.13383 13.3845 9.3465 13.3845Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PencilActionIcon({ className }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <mask id="mask0_1420_6222" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <rect width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_1420_6222)">
        <path
          d="M4.404 20.4961C4.14783 20.4961 3.93317 20.4094 3.76 20.2361C3.58667 20.0629 3.5 19.8483 3.5 19.5921V17.8596C3.5 17.6158 3.54683 17.3833 3.6405 17.1623C3.734 16.9413 3.86283 16.7488 4.027 16.5846L16.6905 3.92684C16.8417 3.78951 17.0086 3.68343 17.1913 3.60859C17.3741 3.53359 17.5658 3.49609 17.7663 3.49609C17.9668 3.49609 18.1609 3.53168 18.3488 3.60284C18.5367 3.67401 18.7032 3.78718 18.848 3.94234L20.0693 5.17884C20.2244 5.32368 20.335 5.49034 20.401 5.67884C20.467 5.86734 20.5 6.05584 20.5 6.24434C20.5 6.44551 20.4657 6.63743 20.397 6.82009C20.3283 7.00293 20.2191 7.16993 20.0693 7.32109L7.4115 19.9691C7.24733 20.1333 7.05475 20.2621 6.83375 20.3556C6.61275 20.4493 6.38033 20.4961 6.1365 20.4961H4.404ZM17.552 7.69034L19 6.25184L17.7443 4.99609L16.3057 6.44409L17.552 7.69034Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}

function RouteIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-6 h-6 ${className}`}
      aria-hidden="true"
    >
      <path
        d="M9 20.5C8.02817 20.5 7.20192 20.1596 6.52125 19.4788C5.84042 18.7981 5.5 17.9718 5.5 17V8.47875C4.91667 8.26208 4.4375 7.93167 4.0625 7.4875C3.6875 7.04333 3.5 6.54967 3.5 6.0065C3.5 5.31033 3.74283 4.7185 4.2285 4.231C4.71417 3.74367 5.30392 3.5 5.99775 3.5C6.69158 3.5 7.28208 3.74367 7.76925 4.231C8.25642 4.7185 8.5 5.31033 8.5 6.0065C8.5 6.54967 8.3125 7.04333 7.9375 7.4875C7.5625 7.93167 7.08333 8.26208 6.5 8.47875V17C6.5 17.6875 6.74517 18.276 7.2355 18.7655C7.72583 19.2552 8.31525 19.5 9.00375 19.5C9.69225 19.5 10.2804 19.2552 10.7683 18.7655C11.2561 18.276 11.5 17.6875 11.5 17V7C11.5 6.02817 11.8404 5.20192 12.5213 4.52125C13.2019 3.84042 14.0282 3.5 15 3.5C15.9718 3.5 16.7981 3.84042 17.4788 4.52125C18.1596 5.20192 18.5 6.02817 18.5 7V15.5213C19.0833 15.7379 19.5625 16.0683 19.9375 16.5125C20.3125 16.9567 20.5 17.4503 20.5 17.9935C20.5 18.6897 20.2572 19.2815 19.7715 19.769C19.2858 20.2563 18.6961 20.5 18.0023 20.5C17.3084 20.5 16.7179 20.2563 16.2308 19.769C15.7436 19.2815 15.5 18.6897 15.5 17.9935C15.5 17.4503 15.6875 16.9525 16.0625 16.5C16.4375 16.0475 16.9167 15.7213 17.5 15.5213V7C17.5 6.3125 17.2548 5.724 16.7645 5.2345C16.2742 4.74483 15.6847 4.5 14.9962 4.5C14.3077 4.5 13.7196 4.74483 13.2318 5.2345C12.7439 5.724 12.5 6.3125 12.5 7V17C12.5 17.9718 12.1596 18.7981 11.4787 19.4788C10.7981 20.1596 9.97183 20.5 9 20.5ZM6 7.5C6.4115 7.5 6.76442 7.35292 7.05875 7.05875C7.35292 6.76442 7.5 6.4115 7.5 6C7.5 5.5885 7.35292 5.23558 7.05875 4.94125C6.76442 4.64708 6.4115 4.5 6 4.5C5.5885 4.5 5.23558 4.64708 4.94125 4.94125C4.64708 5.23558 4.5 5.5885 4.5 6C4.5 6.4115 4.64708 6.76442 4.94125 7.05875C5.23558 7.35292 5.5885 7.5 6 7.5ZM18 19.5C18.4115 19.5 18.7644 19.3529 19.0587 19.0587C19.3529 18.7644 19.5 18.4115 19.5 18C19.5 17.5885 19.3529 17.2356 19.0587 16.9413C18.7644 16.6471 18.4115 16.5 18 16.5C17.5885 16.5 17.2356 16.6471 16.9413 16.9413C16.6471 17.2356 16.5 17.5885 16.5 18C16.5 18.4115 16.6471 18.7644 16.9413 19.0587C17.2356 19.3529 17.5885 19.5 18 19.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function EmptyRoutesIcon() {
  return (
    <svg width="82" height="82" viewBox="0 0 82 82" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-80">
      <g opacity="0.8">
        <path fillRule="evenodd" clipRule="evenodd" d="M54.1351 40.6117L40.5449 54.2006C40.5449 54.2006 45.3778 52.6913 52.3235 55.1065C53.492 55.5126 55.4471 53.5574 55.041 52.3889C52.6258 45.4433 54.1351 40.6117 54.1351 40.6117Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M17.7636 17.8298C26.0149 9.57984 39.4104 9.57984 47.6616 17.8298C55.9116 26.0811 55.9116 39.4765 47.6616 47.7278C39.4104 55.9777 26.0149 55.9777 17.7636 47.7278C9.51368 39.4765 9.51368 26.0811 17.7636 17.8298Z" fill="#E9F3FC"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M44.9279 15.521C45.8863 16.2013 46.8011 16.9714 47.6608 17.8298C55.9108 26.0811 55.9108 39.4765 47.6608 47.7278C39.4096 55.9777 26.0141 55.9777 17.7628 47.7278C17.2773 47.2409 16.8198 46.7374 16.3906 46.2185C24.3882 48.8168 33.5286 46.9411 39.8785 40.5912C46.6512 33.8185 48.3348 23.8709 44.9279 15.521Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M15.9516 16.0182C25.2023 6.76754 40.2224 6.76754 49.473 16.0182C58.7236 25.2688 58.7236 40.2889 49.473 49.5395C40.2224 58.7901 25.2023 58.7901 15.9516 49.5395C6.70102 40.2889 6.70102 25.2688 15.9516 16.0182ZM18.6692 18.7357C26.4195 10.9854 39.0052 10.9854 46.7555 18.7357C54.5057 26.486 54.5057 39.0717 46.7555 46.822C39.0052 54.5723 26.4195 54.5723 18.6692 46.822C10.9189 39.0717 10.9189 26.486 18.6692 18.7357Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M11.4763 43.319C7.07904 34.454 8.57042 23.3994 15.9517 16.0181C22.8833 9.08655 33.0538 7.34918 41.6113 10.806C41.6728 11.5914 41.7048 12.3871 41.7048 13.1904C41.7048 13.8105 41.6856 14.4268 41.6485 15.0392C34.2031 11.2903 24.8833 12.5229 18.6692 18.7356C12.0823 25.3225 11.0932 35.4021 15.7044 43.0384C14.354 43.2229 12.9754 43.319 11.5762 43.319H11.4763Z" fill="#B9CAE3"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L78.0109 69.9242C78.5106 70.4239 77.0897 72.6571 74.8398 74.907C72.59 77.1569 70.3568 78.5778 69.8571 78.0781L51.2841 59.5051Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L76.2479 68.1612C76.7476 68.6609 75.3267 70.8941 73.0768 73.144C70.827 75.3939 68.5938 76.8148 68.0941 76.3151L51.2841 59.5051Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M29.9982 28.7448C29.9687 27.5238 30.4274 26.632 31.1347 26.1093C32.2468 25.2893 33.9086 25.3239 34.8106 26.3399C35.1975 26.7768 35.4217 27.3944 35.4281 28.1759C35.4435 30.0504 34.5646 31.1459 33.7036 32.2798C32.5453 33.8083 31.3883 35.3791 31.2448 38.0018C31.2064 38.7091 31.7497 39.3125 32.4556 39.351C33.1616 39.3894 33.7664 38.8474 33.8048 38.1415C33.9124 36.1491 34.8657 34.9883 35.7459 33.8275C36.9246 32.2734 38.0124 30.723 37.9906 28.1554C37.9778 26.6461 37.4743 25.4814 36.726 24.6384C34.9784 22.6704 31.7689 22.4577 29.6138 24.0464C28.3223 24.9997 27.3832 26.5782 27.4357 28.805C27.4523 29.5123 28.0404 30.0722 28.7477 30.0555C29.4537 30.0389 30.0148 29.452 29.9982 28.7448Z" fill="#8DA5CF"/>
        <path d="M32.406 44.1095C33.2764 44.1095 33.982 43.4039 33.982 42.5336C33.982 41.6632 33.2764 40.9576 32.406 40.9576C31.5356 40.9576 30.8301 41.6632 30.8301 42.5336C30.8301 43.4039 31.5356 44.1095 32.406 44.1095Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M55.4638 10.8867L59.3076 4.48048C59.6715 3.87445 59.4754 3.08648 58.8681 2.72261C58.2621 2.36001 57.4741 2.55604 57.1103 3.16207L53.2665 9.56833C52.9026 10.1756 53.0999 10.9636 53.706 11.3262C54.3133 11.6901 55.1 11.494 55.4638 10.8867Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M61.5248 15.1084L75.6185 4.85845C76.1899 4.44204 76.3168 3.63998 75.9017 3.06854C75.4853 2.49582 74.6832 2.37026 74.1118 2.78539L60.018 13.0354C59.4453 13.4518 59.3197 14.2539 59.7349 14.8253C60.1513 15.398 60.9533 15.5236 61.5248 15.1084Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M63.6442 23.0022L73.8942 20.4397C74.5809 20.268 74.9986 19.5723 74.8269 18.8856C74.6552 18.2001 73.9595 17.7824 73.2728 17.9541L63.0228 20.5166C62.3373 20.6883 61.9196 21.384 62.0913 22.0708C62.263 22.7562 62.9587 23.1739 63.6442 23.0022Z" fill="#8DA5CF"/>
      </g>
    </svg>
  );
}

function SortChevron({ active, direction }) {
  return (
    <span className={`ml-[4px] inline-flex h-[14px] w-[14px] items-center justify-center ${active ? "text-[#483ED1]" : "text-[#BFBFBF]"}`}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        className={direction === "asc" ? "rotate-180" : ""}
      >
        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function ViewActionIcon({ className }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <mask id="mask0_1420_6255" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <rect width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_1420_6255)">
        <path
          d="M12.002 15.577C13.1351 15.577 14.0975 15.1804 14.8892 14.3872C15.6809 13.5941 16.0767 12.6309 16.0767 11.4978C16.0767 10.3646 15.6801 9.40217 14.887 8.6105C14.0938 7.81883 13.1306 7.423 11.9975 7.423C10.8643 7.423 9.90188 7.81958 9.11021 8.61275C8.31854 9.40592 7.92271 10.3691 7.92271 11.5023C7.92271 12.6354 8.31929 13.5978 9.11246 14.3895C9.90563 15.1812 10.8688 15.577 12.002 15.577ZM11.9997 14.2C11.2497 14.2 10.6122 13.9375 10.0872 13.4125C9.56221 12.8875 9.29971 12.25 9.29971 11.5C9.29971 10.75 9.56221 10.1125 10.0872 9.5875C10.6122 9.0625 11.2497 8.8 11.9997 8.8C12.7497 8.8 13.3872 9.0625 13.9122 9.5875C14.4372 10.1125 14.6997 10.75 14.6997 11.5C14.6997 12.25 14.4372 12.8875 13.9122 13.4125C13.3872 13.9375 12.7497 14.2 11.9997 14.2ZM11.9997 18.5C9.89454 18.5 7.96921 17.9384 6.22371 16.8152C4.47821 15.6922 3.08496 14.2148 2.04396 12.3828C1.96063 12.2391 1.89971 12.0943 1.86121 11.9485C1.82288 11.8027 1.80371 11.653 1.80371 11.4995C1.80371 11.346 1.82288 11.1965 1.86121 11.051C1.89971 10.9055 1.96063 10.7609 2.04396 10.6172C3.08496 8.78525 4.47821 7.30775 6.22371 6.18475C7.96921 5.06158 9.89454 4.5 11.9997 4.5C14.1049 4.5 16.0302 5.06158 17.7757 6.18475C19.5212 7.30775 20.9145 8.78525 21.9555 10.6172C22.0388 10.7609 22.0997 10.9057 22.1382 11.0515C22.1765 11.1973 22.1957 11.347 22.1957 11.5005C22.1957 11.654 22.1765 11.8035 22.1382 11.949C22.0997 12.0945 22.0388 12.2391 21.9555 12.3828C20.9145 14.2148 19.5212 15.6922 17.7757 16.8152C16.0302 17.9384 14.1049 18.5 11.9997 18.5Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}

const formatRouteDate = (value) => {
  if (!value) return "–";
  return new Date(value).toLocaleDateString("en-GB");
};

const formatRouteTime = (value) => {
  if (!value) return "–";
  return new Date(value).toLocaleTimeString("el-GR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const COLS = "140px 110px 130px 90px 110px 140px";

const formatDistance = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "–";

  return `${Number(value).toFixed(2)} km`;
};

/** ========= Custom calendar helpers (ported from VideoList) ========= */
const calendarWeekdayLabels = ["M", "T", "W", "T", "F", "S", "S"];

const toIsoDate = (date) => date.toISOString().split("T")[0];
const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isWithinRange = (day, start, end) => {
  if (!day || !start || !end) return false;
  return day > start && day < end;
};

const buildCalendarMatrix = (year, month) => {
  // Monday-based grid
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // Start from Monday of the week containing the 1st
  const start = new Date(firstOfMonth);
  const startOffset = (start.getDay() + 6) % 7; // Mon=0 ... Sun=6
  start.setDate(start.getDate() - startOffset);

  // End at Sunday of the week containing the last day of month
  const end = new Date(lastOfMonth);
  const endOffset = 6 - ((end.getDay() + 6) % 7);
  end.setDate(end.getDate() + endOffset);

  const weeks = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      const date = new Date(cursor);
      const inMonth = date.getMonth() === month;

      week.push({
        date,
        label: String(date.getDate()), // keep numeric; you pad in render
        inMonth,
      });

      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};
/** ================================================================ */

export default function RouteList() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter state (must be declared before `filters` useMemo)
  const [searchTags, setSearchTags] = useState("");
  const [minDistance, setMinDistance] = useState("");
  const [maxDistance, setMaxDistance] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);

  // ISO date strings (YYYY-MM-DD) to match VideoList behaviour
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [ordering, setOrdering] = useState("-uploaded_at");

  const filters = useMemo(() => {
    const f = {};

    if (searchTags.trim()) {
      f.tags = searchTags.trim(); // or "hot,recommended"
    }

    if (maxDistance !== "") {
      f.distance_km__lte = Number(maxDistance);
    }
    if (minDistance !== "") {
      f.distance_km__gte = Number(minDistance);
    }

    if (statusFilter) f.status = statusFilter;

    if (startDate) f.uploaded_at__gte = startDate; // "YYYY-MM-DD"
    if (endDate) f.uploaded_at__lte = endDate;     // "YYYY-MM-DD"
    if (ordering) f.ordering = ordering;

    return f;
  }, [searchTags, minDistance, maxDistance, statusFilter, startDate, endDate, ordering]);

  const { data, isLoading, isError, error } = useFetchRoutes(page, pageSize, filters);
  const routes = useMemo(() => data?.results ?? [], [data]);

  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const uploadRouteMut = useUploadRoute();
  const updateRouteMut = useUpdateRoute();
  const deleteRouteMut = useDeleteRoute();

  const [selectedGeom, setSelectedGeom] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [editingRoute, setEditingRoute] = useState(null);
  const [editTag, setEditTag] = useState("");
  const [editVideoId, setEditVideoId] = useState("");

  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, routeId: null });

  const [activeIcon, setActiveIcon] = useState(null);

  const isIconActive = (routeId, action) =>
    activeIcon?.routeId === routeId && activeIcon?.action === action;

  const [activeFilter, setActiveFilter] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVideoId, setUploadVideoId] = useState("");
  const [uploadValidationError, setUploadValidationError] = useState("");

  const routeInputRef = useRef(null);
  const filterRef = useRef(null);
  const tagsDropdownRef = useRef(null);

  const editTagsDropdownRef = useRef(null);
  const [editTagsOpen, setEditTagsOpen] = useState(false);

  const DIST_MIN = 0;
  const DIST_MAX = 100;

  // Single value 0..100 (used as max distance)
  const [distanceKm, setDistanceKm] = useState(0);

  /** ======= Custom calendar state (ported from VideoList) ======= */
  const today = new Date();
  const [calendarOpenFor, setCalendarOpenFor] = useState(null); // "start" | "end" | null
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
  const [calendarYear, setCalendarYear] = useState(today.getFullYear());
  const calendarDropdownRef = useRef(null);
  const startCalendarTriggerRef = useRef(null);
  const endCalendarTriggerRef = useRef(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const calendarMatrix = useMemo(() => buildCalendarMatrix(calendarYear, calendarMonth), [calendarMonth, calendarYear]);
  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);
  const activeMonthLabel = new Date(calendarYear, calendarMonth).toLocaleString("en-US", { month: "long" });

  const formatCalendarInput = (value) => {
    if (!value) return "";
    const parsed = parseIsoDate(value);
    return parsed ? parsed.toLocaleDateString("en-GB") : "";
  };

  const handleCalendarNav = (direction) => {
    const next = new Date(calendarYear, calendarMonth + direction);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const openCalendar = (target) => {
    // close other dropdowns
    setActiveFilter(null);

    if (calendarOpenFor === target) {
      setCalendarOpenFor(null);
      return;
    }

    const triggerEl = target === "start" ? startCalendarTriggerRef.current : endCalendarTriggerRef.current;
    const containerEl = filterRef.current;

    if (triggerEl && containerEl) {
      const triggerRect = triggerEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      const overlayWidth = 222;
      const padding = 16;
      const desiredLeft = triggerRect.left - containerRect.left;
      const maxLeft = Math.max(padding, containerRect.width - overlayWidth - padding);
      const clampedLeft = Math.max(padding, Math.min(desiredLeft, maxLeft));

      setCalendarPosition({
        top: triggerRect.bottom - containerRect.top + 8,
        left: clampedLeft,
      });
    }

    const referenceIso = target === "start" ? startDate : endDate;
    const fallbackIso = startDate || endDate;
    const referenceDate = parseIsoDate(referenceIso) || parseIsoDate(fallbackIso) || new Date();
    if (referenceDate) {
      setCalendarYear(referenceDate.getFullYear());
      setCalendarMonth(referenceDate.getMonth());
    }

    setCalendarOpenFor(target);
  };

  const handleDaySelect = (date) => {
    if (!calendarOpenFor) return;
    const iso = toIsoDate(date);

    if (calendarOpenFor === "start") {
      setStartDate(iso);
      if (endDate && parseIsoDate(endDate) && parseIsoDate(endDate) < date) setEndDate(iso);
    } else if (calendarOpenFor === "end") {
      setEndDate(iso);
      if (startDate && parseIsoDate(startDate) && parseIsoDate(startDate) > date) setStartDate(iso);
    }

    setCalendarOpenFor(null);
  };

  const clearCalendarSelection = () => {
    setStartDate("");
    setEndDate("");
    setCalendarOpenFor(null);
  };

  useEffect(() => {
    if (!calendarOpenFor) return undefined;

    const onDocClick = (event) => {
      if (
        calendarDropdownRef.current &&
        !calendarDropdownRef.current.contains(event.target) &&
        !startCalendarTriggerRef.current?.contains(event.target) &&
        !endCalendarTriggerRef.current?.contains(event.target)
      ) {
        setCalendarOpenFor(null);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [calendarOpenFor]);
  /** =========================================================== */

  // Close status dropdown on outside click
  useEffect(() => {
    if (!statusFilterOpen) return undefined;
    const onMouseDown = (e) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target)) {
        setStatusFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [statusFilterOpen]);

  // Close dropdowns on outside click (tags/distance)
  useEffect(() => {
    const onMouseDown = (event) => {
      const target = event.target;

      if (activeFilter === "tags" && tagsDropdownRef.current && !tagsDropdownRef.current.contains(target)) {
        setActiveFilter(null);
        return;
      }

      if (!filterRef.current) return;
      if (!filterRef.current.contains(target)) setActiveFilter(null);
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [activeFilter]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (!editingRoute || !editTagsOpen) return;

    const onMouseDown = (e) => {
      const t = e.target;
      if (
        editTagsDropdownRef.current &&
        !editTagsDropdownRef.current.contains(t)
      ) {
        setEditTagsOpen(false);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [editingRoute, editTagsOpen]);

  const uniqueTags = useMemo(() => {
    const defaultTags = ["hot", "recommended", "new", "easy", "difficult", "urban"];
    const s = new Set(defaultTags);
    (routes || []).forEach((r) => (r.tags || []).forEach((t) => t && s.add(t)));
    return Array.from(s);
  }, [routes]);

  const displayedRoutes = routes;

  const hasFilters = !!(searchTags || minDistance || maxDistance || statusFilter || startDate || endDate);
  const emptyMessage = hasFilters ? "No routes match your filters." : "No routes found.";

  const clearFilters = () => {
    setSearchTags("");
    setMinDistance("");
    setMaxDistance("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setDistanceKm(0);
    setCalendarOpenFor(null);
    setActiveFilter(null);
    setStatusFilterOpen(false);
  };

  const getSortState = (field) => {
    if (ordering === field) return { active: true, direction: "asc" };
    if (ordering === `-${field}`) return { active: true, direction: "desc" };
    return { active: false, direction: "desc" };
  };

  const toggleSort = (field) => {
    setOrdering((current) => (current === `-${field}` ? field : `-${field}`));
  };

  const SortHeader = ({ field, children }) => {
    const sort = getSortState(field);
    return (
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className={`inline-flex items-center text-left transition-colors hover:text-[#483ED1] ${sort.active ? "text-[#483ED1]" : "text-[#828282]"}`}
        title={`Sort by ${children}`}
      >
        <span>{children}</span>
        <SortChevron active={sort.active} direction={sort.direction} />
      </button>
    );
  };

  const closeUploadModal = () => {
    setUploadOpen(false);
    setUploadValidationError("");
  };

  // Reset upload form when modal closes
  useEffect(() => {
    if (uploadOpen) return;
    setUploadFile(null);
    setUploadVideoId("");
    setUploadValidationError("");
    if (routeInputRef.current) routeInputRef.current.value = "";
  }, [uploadOpen]);

  const handleRouteFileChange = (event) => {
    const selected = event.target.files?.[0] ?? null;
    setUploadFile(selected);
    setUploadValidationError("");
  };

  const handleUpload = (event) => {
    event.preventDefault();
    setUploadValidationError("");

    if (!uploadFile) {
      setUploadValidationError("Please select a route (.gpx) file.");
      return;
    }

    const parsedVideoId = uploadVideoId.trim() ? Number(uploadVideoId) : null;
    if (uploadVideoId.trim() && Number.isNaN(parsedVideoId)) {
      setUploadValidationError("Video ID must be a number.");
      return;
    }

    uploadRouteMut.mutate(
      { file: uploadFile, videoId: parsedVideoId },
      {
        onSuccess: () => closeUploadModal(),
        onError: (err) => {
          const body = err?.response?.data;
          const message = typeof body === "string" ? body : body?.detail;
          setUploadValidationError(message || "Upload failed. Please try again.");
        },
      }
    );
  };

  const handleDelete = (id) => setDeleteConfirm({ isOpen: true, routeId: id });

  const confirmDelete = () => {
    const { routeId } = deleteConfirm;

    if (selectedId === routeId) {
      setSelectedGeom(null);
      setSelectedId(null);
    }

    deleteRouteMut.mutate(routeId);
    setDeleteConfirm({ isOpen: false, routeId: null });
    setActiveIcon(null);
  };

  const openEditModal = (route) => {
    setEditingRoute(route);

    const tag = (route.tags || []).filter(Boolean)[0] || "";
    setEditTag(tag);
    setEditVideoId(route.video?.toString() ?? "");

    setSelectedGeom(null);
    setSelectedId(null);
  };

  const closeEditModal = () => {
    setEditingRoute(null);
    setEditTag("");
    setEditVideoId("");
    setActiveIcon(null);
  };

  const handleUpdateRoute = (event) => {
    event.preventDefault();
    if (!editingRoute) return;

    updateRouteMut.mutate(
      {
        id: editingRoute.id,
        tags: editTag ? [editTag] : [],
        video: editVideoId ? Number(editVideoId) : null
      },
      { onSuccess: closeEditModal }
    );
  };

  // Loading / error states
  if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Spinner className="h-12 w-12 text-camber-blue-500 mx-auto mb-4" />
            <p className="text-camber-gray-600">Loading routes...</p>
          </div>
        </div>
      );
    }

  if (isError) {
    const body = error?.response?.data;
    const message = typeof body === "string" ? body : JSON.stringify(body, null, 2);

    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] p-6">
        <pre className="text-red-600 font-medium whitespace-pre-wrap bg-white rounded-lg p-4 shadow-lg">
          Error loading routes:
          {"\n"}
          {message}
        </pre>
      </div>
    );
  }

  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-[#F4F7FB] font-['Open_Sans']">
      {/* Upload Modal */}
      {uploadOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#131313]/[0.69]" onClick={closeUploadModal}>
          <div className="w-[435px] min-h-[340px] bg-white rounded-[8px] shadow-xl overflow-visible" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleUpload} className="px-[24px] pt-[24px] pb-[32px] flex flex-col gap-[24px]">
              {/* Header */}
              <div className="flex items-center gap-[16px]">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="w-[40px] h-[40px] rounded-[8px] bg-[#C9D8FC] flex items-center justify-center"
                  aria-label="Back"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5 6.5L9.5 12L14.5 17.5" stroke="#3C11BA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className="text-[18px] font-semibold leading-[155%] text-[#242424]">Upload Route</div>
              </div>

              {/* Inputs */}
              <div className="flex flex-col gap-[32px]">
                {/* Route file */}
                <div className="flex flex-col gap-[8px]">
                  <div className="text-[14px] leading-[155%] text-[#212529]">Route</div>

                  <div className="flex items-center gap-[8px]">
                    <input
                      ref={routeInputRef}
                      type="file"
                      accept=".gpx"
                      onChange={handleRouteFileChange}
                      disabled={uploadRouteMut.isPending}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => routeInputRef.current?.click()}
                      disabled={uploadRouteMut.isPending}
                      className="box-border shrink-0 w-[110px] h-[32px] bg-[#FBFBFB] border border-[#CDCDCD] rounded-[4px] px-[24px] py-[8px] flex items-center justify-center whitespace-nowrap text-[14px] leading-[155%] text-[#0B0B0B] disabled:opacity-50"
                    >
                      Select file
                    </button>
                    <div className="flex-1 min-w-0 h-[40px] bg-white border border-[#E0E2E5] rounded-[8px] px-[8px] flex items-center overflow-hidden">
                      <div className="text-[14px] leading-[155%] text-[#ADB5BD] truncate">{uploadFile?.name || "File not selected"}</div>
                    </div>
                  </div>
                </div>

                {/* Video ID */}
                <div className="flex flex-col gap-[8px]">
                  <div className="text-[14px] leading-[155%] text-[#212529]">Link Video ID</div>
                  <div className="w-full h-[40px] bg-white border border-[#E0E2E5] rounded-[8px] px-[8px] flex items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={uploadVideoId}
                      onChange={(e) => setUploadVideoId(e.target.value)}
                      placeholder="Video ID"
                      className="w-full bg-transparent outline-none text-[14px] leading-[155%] text-[#0B0B0B] placeholder:text-[#ADB5BD]"
                    />
                  </div>
                </div>
              </div>

              {uploadValidationError && <div className="-mt-[12px] text-[12px] leading-[155%] text-red-700">{uploadValidationError}</div>}

              {/* Actions */}
              <div className="mt-auto flex items-center justify-between gap-[16px]">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="w-[179px] h-[40px] bg-[#E2DCF8] rounded-[4px] text-[16px] leading-[155%] text-[#2C1B6B]"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={uploadRouteMut.isPending || !uploadFile}
                  className="w-[180px] h-[40px] bg-[#5937D6] rounded-[4px] text-[16px] leading-[155%] text-[#E3DDF8] disabled:opacity-50"
                >
                  {uploadRouteMut.isPending ? "Uploading…" : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header card */}
      <div className="pt-[32px] px-[32px]">
        <div className="w-full h-[89px] bg-[#FDFEFF] rounded-[8px] px-[24px] py-[16px] flex items-center justify-between">
          <div className="flex flex-col justify-between h-full">
            <div className="flex items-center gap-2 h-[31px]">
              <span className="text-[20px] font-extrabold leading-[155%] text-camber-text">
                Routes
              </span>
              <RouteIcon className="text-camber-text" />
            </div>
            <p className="text-[14px] font-light leading-[155%] text-[#333333]">Upload and manage inspection routes</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              disabled={uploadRouteMut.isPending}
              className="flex h-10 items-center justify-center gap-1 rounded-[4px] bg-[#E3DDF8] px-4 text-[14px] font-normal leading-[155%] text-[#2C1B6B] disabled:opacity-60"
            >
              <ArrowUpTrayIcon className="h-5 w-5 text-[#2C1B6B]" />
              <span>{uploadRouteMut.isPending ? "Uploading…" : "Upload Route"}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate("/routes/map?mode=plan")}
              className="flex h-10 items-center justify-center gap-1 rounded-[4px] bg-[#5937D6] px-4 text-[14px] font-normal leading-[155%] text-[#FAFAFA]"
            >
              <PlusIcon className="h-5 w-5 text-[#FAFAFA]" />
              <span>Plan Route</span>
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="pt-[16px] px-[32px]">
        <div className={`w-full bg-[#FDFEFF] rounded-[8px] p-[8px] ${displayedRoutes.length === 0 ? "min-h-[529px]" : ""}`}>
          {/* Filters row */}
          <div
            ref={filterRef}
            className="w-full min-h-[64px] bg-[#F4F7FA] rounded-[4px] px-[16px] py-[16px] flex flex-wrap items-center justify-between gap-y-[12px] relative"
          >
            <div className="flex flex-wrap items-center gap-[8px]">
              {/* Status */}
              <div ref={statusFilterRef} className="relative w-[130px] h-[32px]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCalendarOpenFor(null);
                    setActiveFilter(null);
                    setStatusFilterOpen((v) => !v);
                  }}
                  className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between"
                  aria-haspopup="listbox"
                  aria-expanded={statusFilterOpen}
                >
                  <span className={`text-[12px] leading-[155%] ${statusFilter ? "text-[#333333]" : "text-[#939393]"}`}>
                    {statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : "Status"}
                  </span>
                  <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M7 10L12 15L17 10" fill="currentColor" />
                    </svg>
                  </span>
                </button>

                {statusFilterOpen && (
                  <div className="absolute left-0 top-[36px] w-[128px] bg-white border border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20">
                    <div className="w-full py-[8px]">
                      <div className="w-full flex flex-col items-center gap-[8px]">
                        <button
                          type="button"
                          onClick={() => { setStatusFilter(""); setStatusFilterOpen(false); }}
                          className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                          role="option"
                          aria-selected={statusFilter === ""}
                        >
                          All
                        </button>
                        <div className="w-full border-t border-[#E0E2E5]" />
                        <button
                          type="button"
                          onClick={() => { setStatusFilter("green"); setStatusFilterOpen(false); }}
                          className="w-full flex items-center justify-center"
                          role="option"
                          aria-selected={statusFilter === "green"}
                        >
                          <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#EBFBEE] bg-[#EBFBEE] text-[12px] leading-[155%] text-[#2F9E44]">Green</span>
                        </button>
                        <div className="w-full border-t border-[#E0E2E5]" />
                        <button
                          type="button"
                          onClick={() => { setStatusFilter("yellow"); setStatusFilterOpen(false); }}
                          className="w-full flex items-center justify-center"
                          role="option"
                          aria-selected={statusFilter === "yellow"}
                        >
                          <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFF3BF] bg-[#FFF3BF] text-[12px] leading-[155%] text-[#9A6700]">Yellow</span>
                        </button>
                        <div className="w-full border-t border-[#E0E2E5]" />
                        <button
                          type="button"
                          onClick={() => { setStatusFilter("orange"); setStatusFilterOpen(false); }}
                          className="w-full flex items-center justify-center"
                          role="option"
                          aria-selected={statusFilter === "orange"}
                        >
                          <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFE8CC] bg-[#FFE8CC] text-[12px] leading-[155%] text-[#D9480F]">Orange</span>
                        </button>
                        <div className="w-full border-t border-[#E0E2E5]" />
                        <button
                          type="button"
                          onClick={() => { setStatusFilter("red"); setStatusFilterOpen(false); }}
                          className="w-full flex items-center justify-center"
                          role="option"
                          aria-selected={statusFilter === "red"}
                        >
                          <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFE3E3] bg-[#FFE3E3] text-[12px] leading-[155%] text-[#C92A2A]">Red</span>
                        </button>
                        <div className="w-full border-t border-[#E0E2E5]" />
                        <button
                          type="button"
                          onClick={() => { setStatusFilter("black"); setStatusFilterOpen(false); }}
                          className="w-full flex items-center justify-center"
                          role="option"
                          aria-selected={statusFilter === "black"}
                        >
                          <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#343A40] bg-[#343A40] text-[12px] leading-[155%] text-[#FFFFFF]">Black</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div ref={tagsDropdownRef} className="relative w-[130px] h-[32px]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCalendarOpenFor(null);
                    setStatusFilterOpen(false);
                    setActiveFilter((prev) => (prev === "tags" ? null : "tags"));
                  }}
                  className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[10px] flex items-center justify-between min-w-0"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-[12px] leading-[155%] text-[#939393] shrink-0">Tags</span>
                    <span
                      className={`text-[12px] leading-[155%] min-w-0 flex-1 truncate ${searchTags ? "text-[#333333]" : "text-[#939393]"}`}
                      title={searchTags || ""}
                    >
                      {searchTags || ""}
                    </span>
                  </div>

                  <span className="ml-2 flex items-center justify-center shrink-0 text-[#939393]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M7 10L12 15L17 10" fill="currentColor" />
                    </svg>
                  </span>
                </button>

                {activeFilter === "tags" && (
                  <div className="absolute left-0 top-[32px] w-[128px] bg-white border-t border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20">
                    <div className="max-h-[160px] overflow-y-auto">
                      <div className="flex flex-col items-center justify-center px-[7px] py-[8px] gap-[8px]">
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTags("");
                            setActiveFilter(null);
                          }}
                          className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                          role="option"
                          aria-selected={searchTags === ""}
                        >
                          All
                        </button>

                        {uniqueTags.length > 0 ? (
                          uniqueTags.map((t) => (
                            <React.Fragment key={t}>
                              <div className="w-full border-t border-[#E0E2E5]" />
                              <button
                                type="button"
                                onClick={() => {
                                  setSearchTags(t);
                                  setActiveFilter(null);
                                }}
                                className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%] truncate"
                                role="option"
                                aria-selected={searchTags === t}
                                title={t}
                              >
                                {t}
                              </button>
                            </React.Fragment>
                          ))
                        ) : (
                          <>
                            <div className="w-full border-t border-[#E0E2E5]" />
                            <div className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]">
                              No tags
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Distance (single thumb => sets maxDistance) */}
              <div className="relative w-[148px] h-[32px]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCalendarOpenFor(null);
                    setActiveFilter((prev) => (prev === "distance" ? null : "distance"));
                  }}
                  className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[10px] flex items-center justify-between min-w-0"
                  aria-haspopup="dialog"
                  aria-expanded={activeFilter === "distance"}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-[12px] leading-[155%] text-[#939393] shrink-0">Distance</span>
                    <span className="text-[12px] leading-[155%] min-w-0 flex-1 truncate text-[#333333]">{distanceKm} km</span>
                  </div>

                  <span className="ml-2 flex items-center justify-center shrink-0 text-[#939393]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M7 10L12 15L17 10" fill="currentColor" />
                    </svg>
                  </span>
                </button>

                {activeFilter === "distance" && (
                  <div className="absolute left-0 top-[36px] w-[260px] bg-white border border-[#E0E2E5] rounded-[10px] shadow-[0px_2px_10px_rgba(0,0,0,0.18)] z-20 px-4 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <Range
                          values={[distanceKm]}
                          step={1}
                          min={DIST_MIN}
                          max={DIST_MAX}
                          onChange={(values) => {
                            const v = values[0] ?? 0;
                            setDistanceKm(v);
                            setMinDistance("");
                            setMaxDistance(String(v));
                          }}
                          renderTrack={({ props, children }) => {
                            const pct = ((distanceKm - DIST_MIN) / (DIST_MAX - DIST_MIN)) * 100;
                            return (
                              <div onMouseDown={props.onMouseDown} onTouchStart={props.onTouchStart} className="w-full h-[11px] flex">
                                <div ref={props.ref} className="relative w-full h-[6px] bg-[#D9D9D9] rounded-[55px] self-center">
                                  <div
                                    className="absolute inset-y-0 left-0 rounded-[55px]"
                                    style={{
                                      width: `${pct}%`,
                                      backgroundImage: "linear-gradient(180deg, #DA3272 0%, #5438CE 100%)",
                                    }}
                                  />
                                  {children}
                                </div>
                              </div>
                            );
                          }}
                          renderThumb={({ props }) => (
                            <div
                              {...props}
                              className="w-[16px] h-[16px] rounded-full outline-none"
                              style={{
                                ...props.style,
                                backgroundImage: "linear-gradient(180deg, #DA3272 0%, #5438CE 100%)",
                                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.25)",
                              }}
                            />
                          )}
                        />
                      </div>

                      <div className="w-[52px] text-right text-[14px] leading-[155%] text-[#AAACAF]">{distanceKm} km</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Start Date (custom calendar) */}
              <div ref={startCalendarTriggerRef} className="relative w-[129px] h-[32px]">
                <button
                  type="button"
                  onClick={() => openCalendar("start")}
                  aria-haspopup="true"
                  aria-expanded={calendarOpenFor === "start"}
                  className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
                >
                  <span className={startDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                    {startDate ? formatCalendarInput(startDate) : "Start Date"}
                  </span>
                  <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M7 10L12 15L17 10" fill="currentColor" />
                    </svg>
                  </span>
                </button>
              </div>

              {/* End Date (custom calendar) */}
              <div ref={endCalendarTriggerRef} className="relative w-[123px] h-[32px]">
                <button
                  type="button"
                  onClick={() => openCalendar("end")}
                  aria-haspopup="true"
                  aria-expanded={calendarOpenFor === "end"}
                  className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
                >
                  <span className={endDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                    {endDate ? formatCalendarInput(endDate) : "End Date"}
                  </span>
                  <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M7 10L12 15L17 10" fill="currentColor" />
                    </svg>
                  </span>
                </button>
              </div>

              {/* Calendar dropdown overlay */}
              {calendarOpenFor && (
                <div
                  ref={calendarDropdownRef}
                  className="absolute z-30 w-[222px] h-[249px] bg-white border border-[#E0E2E5] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] rounded-[4px]"
                  style={{ top: calendarPosition.top, left: calendarPosition.left }}
                >
                  {/* Header: padding 10px 14px, gap 8px */}
                  <div className="flex flex-col items-start px-[14px] py-[10px] gap-[8px]">
                    <div className="w-full h-[22px] flex items-center justify-between">
                      {/* Month label: 16/22 regular */}
                      <div className="text-[16px] leading-[22px] font-normal text-[#333333]">
                        {activeMonthLabel}
                      </div>

                      {/* Year controls: 11px arrows, 12/16 year */}
                      <div className="h-[16px] flex items-center justify-center gap-[4px]">
                        <button
                          type="button"
                          onClick={() => handleCalendarNav(-1)}
                          className="w-[11px] h-[11px] flex items-center justify-center"
                          aria-label="Previous month"
                        >
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 2.5L4.5 6L7.5 9.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>

                        <div className="w-[28px] text-center text-[12px] leading-[16px] font-normal text-[#333333]">
                          {calendarYear}
                        </div>

                        <button
                          type="button"
                          onClick={() => handleCalendarNav(1)}
                          className="w-[11px] h-[11px] flex items-center justify-center"
                          aria-label="Next month"
                        >
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.5 9.5L7.5 6L4.5 2.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Calendar grid area */}
                  <div className="w-full flex flex-col items-start px-[7px]">
                    {/* Weekday labels: 10/14, opacity 0.7, lowercase */}
                    <div className="w-[208px] h-[32px] flex items-start justify-between opacity-70">
                      {calendarWeekdayLabels.map((label, idx) => (
                        <div key={`${label}-${idx}`} className="w-[30px] h-[32px] p-[4px] flex items-start justify-center">
                          <div className="w-[22px] h-[24px] flex items-center justify-center">
                            <span className="text-[10px] leading-[14px] font-normal text-[#333333]">
                              {label.toLowerCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Weeks */}
                    <div className="w-[208px] flex flex-col items-start">
                      {calendarMatrix.map((week, weekIndex) => (
                        <div key={weekIndex} className="w-[208px] h-[32px] flex items-start justify-between">
                          {week.map((day, dayIndex) => {
                            const isSelectedStart = isSameDay(day.date, parsedStartDate);
                            const isSelectedEnd = isSameDay(day.date, parsedEndDate);

                            // Keep your range logic, but constrain highlight to the inner pill
                            const inRange = isWithinRange(day.date, parsedStartDate, parsedEndDate);

                            const isSelected = isSelectedStart || isSelectedEnd;

                            // Figma: out-of-month opacity 0.3
                            const cellOpacity = day.inMonth ? "" : "opacity-30";

                            const pillClass = isSelected
                              ? "bg-gradient-to-b from-[#DA3272] to-[#5438CE] text-white"
                              : inRange
                                ? "bg-[#F3E8FF] text-[#333333]"
                                : "bg-transparent text-[#333333]";

                            return (
                              <button
                                key={`${weekIndex}-${dayIndex}`}
                                type="button"
                                onClick={() => handleDaySelect(day.date)}
                                className={`w-[30px] h-[32px] p-[4px] flex items-start justify-center ${cellOpacity}`}
                              >
                                {/* Inner pill: 22x22 for selected, 22x24 otherwise (matches figma proportions) */}
                                <span
                                  className={`${
                                    isSelected ? "w-[22px] h-[22px]" : "w-[22px] h-[24px]"
                                  } rounded-[99px] flex items-center justify-center ${pillClass}`}
                                >
                                  <span className={`text-[10px] leading-[14px] font-normal ${isSelected ? "text-white" : ""}`}>
                                    {day.label.padStart(2, "0")}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer: Clear (left) */}
                  <div className="px-[14px] py-[10px] flex items-center justify-start">
                    <button type="button" onClick={clearCalendarSelection} className="text-[12px] leading-[16px] text-[#5937D6]">
                      Clear
                    </button>
                  </div>
                </div>
              )}

            </div>

            <div className="flex items-center justify-end gap-[12px] ml-auto">
              {(searchTags || minDistance || maxDistance || statusFilter || startDate || endDate) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[12px] leading-[155%] text-[#5937D6] hover:underline"
                >
                  Clear
                </button>
              )}

              {totalCount > 0 && (
                <Pagination
                  variant="compact"
                  currentPage={page}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={(next) => setPage(next)}
                  onPageSizeChange={(nextSize) => {
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                />
              )}
            </div>
          </div>

          {/* Table */}
          <div className="mt-[12px]">
            {/* Header */}
            <div className="h-[34px] border-b border-[#E0E2E5] px-[24px]">
              <div
                className="grid h-full items-center text-[12px] leading-[155%] text-[#828282]"
                style={{ gridTemplateColumns: COLS }}
              >
                <div>Name</div>
                <div><SortHeader field="uploaded_at">Date</SortHeader></div>
                <div>Time</div>
                <div><SortHeader field="distance_km">Distance</SortHeader></div>
                <div>Surface</div>
                <div>Related Video</div>
              </div>
            </div>

            {displayedRoutes.length === 0 ? (
              <div className="h-[420px] flex flex-col items-center justify-center">
                <EmptyRoutesIcon />
                <div className="mt-[18px] text-[12px] leading-[155%] text-[#969696]">
                  {emptyMessage}
                </div>
              </div>
            ) : (
              displayedRoutes.map((route) => {
                const actionBtnBase = "flex h-[40px] w-[39px] items-center justify-center rounded-[8px] transition-colors duration-200";
                const actionBtnIdle = "bg-transparent text-[#BBBABB]";
                const actionBtnHover = "hover:bg-[#D4DAFE] hover:text-[#483ED1]";
                const actionBtnActive = "bg-[#D4DAFE] text-[#483ED1]";

                return (
                  <div
                    key={route.id}
                    className="group cursor-pointer box-border flex h-[52px] items-center border-b border-[#E0E2E5] bg-[#FDFEFF]
                              pl-[24px] pr-[12px] transition-colors hover:bg-[#F3F5FE] hover:border-[#C9D8FC]"
                  >
                    {/* Data columns (force same width/alignment as header) */}
                    <div
                      className="grid flex-1 items-center text-[14px] leading-[155%] text-black"
                      style={{ gridTemplateColumns: COLS }}
                    >
                      <div className="truncate">
                        {route.name?.trim() ? route.name : `Route #${route.id}`}
                      </div>

                      <div className="tabular-nums">{formatRouteDate(route.uploaded_at)}</div>
                      <div className="tabular-nums">{formatRouteTime(route.uploaded_at)}</div>
                      <div className="tabular-nums">{formatDistance(route.distance_km)}</div>
                      <div>
                        <SurfaceRatingBadge
                          status={route.status}
                          weightedScore={route.weighted_malfunction_score}
                          weightedPointsPerKm={route.weighted_malfunction_points_per_km}
                          details={route.surface_rating_details}
                        />
                      </div>
                      <div className="truncate">{route.video ? `Video #${route.video}` : "–"}</div>
                    </div>

                    {/* Actions (fixed width) */}
                    <div className="flex h-[40px] w-[141px] items-center justify-end gap-3">
                      {/* DELETE */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIcon({ routeId: route.id, action: "delete" });
                          handleDelete(route.id);
                        }}
                        className={[
                          actionBtnBase,
                          actionBtnIdle,
                          actionBtnHover,
                          isIconActive(route.id, "delete") ? actionBtnActive : "",
                        ].join(" ")}
                        aria-label="Delete"
                      >
                        <TrashActionIcon className="h-[18px] w-[18px]" />
                      </button>

                      {/* EDIT */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIcon({ routeId: route.id, action: "edit" });
                          openEditModal(route);
                        }}
                        className={[
                          actionBtnBase,
                          actionBtnIdle,
                          actionBtnHover,
                          isIconActive(route.id, "edit") ? actionBtnActive : "",
                        ].join(" ")}
                        aria-label="Edit"
                      >
                        <PencilActionIcon className="h-[24px] w-[24px]" />
                      </button>

                      {/* VIEW */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIcon({ routeId: route.id, action: "view" });
                          setSelectedGeom(route.geom);
                          setSelectedId(route.id);
                        }}
                        className={[
                          actionBtnBase,
                          actionBtnIdle,
                          actionBtnHover,
                          isIconActive(route.id, "view") ? actionBtnActive : "",
                        ].join(" ")}
                        aria-label="View"
                      >
                        <ViewActionIcon className="h-[24px] w-[24px]" />
                      </button>
                    </div>
                  </div>
                );
              })

            )}
          </div>

          
        </div>
        {/* Footer */}
          <div className="w-full mt-[12px] px-[8px] pb-[4px] flex items-center justify-between">
            <div className="text-[12px] leading-[155%] text-[#969696]">
              {totalCount === 0 ? "Showing 0 results" : `Showing ${startItem}-${endItem} results`}
            </div>
          </div>

        {deleteRouteMut.isError && (
          <div className="rounded-[8px] border border-[#F8D7DA] bg-[#FDECEA] px-4 py-3 text-sm font-medium text-[#B00020]">
            Delete failed: {deleteRouteMut.error?.response?.data?.detail || deleteRouteMut.error?.message}
          </div>
        )}
      </div>

      {/* Map preview modal */}
      {selectedGeom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-[1100px] rounded-[16px] bg-white p-6 shadow-[0px_20px_30px_rgba(0,0,0,0.2)]">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-[18px] font-semibold text-[#333333]">Route Preview {selectedId ? `#${selectedId}` : ""}</h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedGeom(null);
                  setSelectedId(null);
                  setActiveIcon(null);
                }}
                className="text-[12px] font-semibold text-[#4A19DD]"
              >
                Close
              </button>
            </div>
            <div className="h-[420px] rounded-[12px] border border-[#E0E2E5]">
              <RouteOnMap key={JSON.stringify(selectedGeom.coordinates)} geom={selectedGeom} height={420} />
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingRoute && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#131313]/[0.69]"
          onClick={closeEditModal}
        >
          <div
            className="w-[435px] h-[340px] bg-white rounded-[8px] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <form
              onSubmit={handleUpdateRoute}
              className="h-full px-[24px] pt-[24px] pb-[32px] flex flex-col gap-[24px]"
            >
              {/* Header */}
              <div className="flex items-center gap-[16px]">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="w-[40px] h-[40px] rounded-[8px] bg-[#C9D8FC] flex items-center justify-center"
                  aria-label="Back"
                >
                  {/* arrow_back_ios_new */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M14.5 6.5L9.5 12L14.5 17.5"
                      stroke="#3C11BA"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <div className="text-[18px] font-semibold leading-[155%] text-[#242424]">
                  Edit Route #{editingRoute.id}
                </div>
              </div>

              {/* Body */}
              <div className="flex flex-col gap-[24px] w-[387px]">
                {/* Tags */}
                <div className="flex flex-col gap-[8px] relative" ref={editTagsDropdownRef}>
                  <label className="text-[14px] leading-[155%] text-[#212529]">Tags</label>

                  <button
                    type="button"
                    onClick={() => setEditTagsOpen((v) => !v)}
                    className="w-[387px] h-[40px] bg-white border border-[#E0E2E5] rounded-[8px] px-[12px] flex items-center justify-between"
                  >
                    <span className={`text-[14px] leading-[155%] truncate ${editTag ? "text-[#0B0B0B]" : "text-[#ADB5BD]"}`}>
                      {editTag || "Select tag"}
                    </span>

                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M7 10L12 15L17 10" fill="currentColor" />
                    </svg>
                  </button>

                  {editTagsOpen && (
                    <div className="absolute left-0 top-[calc(40px+8px)] w-[387px] bg-white border border-[#E0E2E5] rounded-[8px] shadow-[0px_2px_10px_rgba(0,0,0,0.18)] overflow-hidden z-[80]">
                      <div className="max-h-[180px] overflow-y-auto py-[8px]">
                        {uniqueTags.length ? (
                          uniqueTags.map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => {
                                setEditTag(t);
                                setEditTagsOpen(false);
                              }}
                              className={`w-full px-[12px] py-[8px] text-left text-[14px] leading-[155%] hover:bg-[#F3F5FE] ${
                                editTag === t ? "bg-[#F3F5FE] text-[#5937D6]" : "text-[#0B0B0B]"
                              }`}
                            >
                              {t}
                            </button>
                          ))
                        ) : (
                          <div className="px-[12px] py-[8px] text-[14px] text-[#939393]">No tags available</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>


                {/* Linked Video ID */}
                <div className="flex flex-col gap-[8px]">
                  <label className="text-[14px] leading-[155%] text-[#212529]">
                    Linked Video ID (Optional)
                  </label>
                  <div className="w-[387px] h-[40px] bg-white border border-[#E0E2E5] rounded-[8px] px-[12px] py-[10px] flex items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={editVideoId}
                      onChange={(e) => setEditVideoId(e.target.value)}
                      placeholder="e.g. 16"
                      className="w-full bg-transparent outline-none text-[14px] leading-[155%] text-[#0B0B0B] placeholder:text-[#ADB5BD]"
                    />
                  </div>
                </div>

                {updateRouteMut.isError && (
                  <div className="text-[12px] leading-[155%] text-red-700">
                    {updateRouteMut.error?.response?.data?.detail || "Update failed"}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-auto flex items-center justify-between gap-[131px] w-[387px]">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="w-[179px] h-[40px] bg-[#E2DCF8] rounded-[4px] text-[16px] leading-[155%] text-[#2C1B6B]"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={updateRouteMut.isPending}
                  className="w-[180px] h-[40px] bg-[#5937D6] rounded-[4px] text-[16px] leading-[155%] text-[#E3DDF8] disabled:opacity-50"
                >
                  {updateRouteMut.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => {
          setDeleteConfirm({ isOpen: false, routeId: null });
          setActiveIcon(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Route"
        message="Delete this route? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteRouteMut.isPending}
      />
    </div>
  );
}
